import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, ArrowRight, AlertTriangle, TrendingUp, Shield, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface OptionScore {
  name: string;
  score: number;
  isDominant: boolean;
  koFailed: boolean;
}

interface Phase3TransitionProps {
  onContinue: () => void;
  isDisabled?: boolean;
  dominantOptionName?: string;
  isConstraintBreak?: boolean;
  optionScores?: OptionScore[];
  alignmentScore?: number;
  thesisSummary?: string;
}

export const Phase3Transition = ({ 
  onContinue, 
  isDisabled, 
  dominantOptionName, 
  isConstraintBreak,
  optionScores,
  alignmentScore,
  thesisSummary,
}: Phase3TransitionProps) => {
  const sortedScores = optionScores ? [...optionScores].sort((a, b) => b.score - a.score) : [];
  const hasScores = sortedScores.length > 0;

  return (
    <div className={cn(
      "rounded-xl border-2 bg-gradient-to-br from-primary/5 via-transparent to-primary/3 p-6 md:p-8 transition-all duration-300",
      isDisabled 
        ? "border-border opacity-60" 
        : isConstraintBreak
          ? "border-destructive/30 hover:border-destructive/50 hover:shadow-lg hover:shadow-destructive/5"
          : "border-primary/30 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5"
    )}>
      {/* Thesis Summary */}
      {thesisSummary && !isDisabled && (
        <div className="mb-5 pb-5 border-b border-border/50">
          <p className="text-sm italic text-muted-foreground font-serif leading-relaxed border-l-2 border-primary/30 pl-3">
            „{thesisSummary}"
          </p>
        </div>
      )}

      {/* Score Summary + Alignment Score */}
      {hasScores && !isDisabled && (
        <div className="mb-5 pb-5 border-b border-border/50">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Option Scores Table */}
            <div className="flex-1 space-y-1.5">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Matrix-Ergebnis
              </p>
              {sortedScores.map((opt, idx) => {
                const isScoreLeader = idx === 0;
                const isRecommended = opt.isDominant;
                const isDivergent = isScoreLeader && !isRecommended;
                return (
                  <div 
                    key={opt.name} 
                    className={cn(
                      "flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                      isRecommended 
                        ? "bg-primary/10 border border-primary/20" 
                        : "bg-muted/40"
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isRecommended && (
                        <TrendingUp className="h-3.5 w-3.5 text-primary shrink-0" />
                      )}
                      <span className={cn(
                        "truncate",
                        isRecommended ? "font-semibold text-foreground" : "text-muted-foreground"
                      )}>
                        {opt.name}
                      </span>
                      {/* Role badges when divergent */}
                      {isDivergent && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border shrink-0">
                          Score Leader
                        </span>
                      )}
                      {isRecommended && isScoreLeader === false && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 shrink-0">
                          Empfohlen
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={cn(
                        "font-mono text-sm font-semibold tabular-nums",
                        isRecommended ? "text-primary" : "text-muted-foreground"
                      )}>
                        {opt.score.toFixed(1)}
                      </span>
                      {opt.koFailed ? (
                        <XCircle className="h-3.5 w-3.5 text-destructive" />
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Alignment Score Badge */}
            {alignmentScore != null && (
              <div className="flex md:flex-col items-center justify-center gap-1 md:min-w-[100px] p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                <span className="text-3xl font-bold text-primary font-mono tabular-nums">
                  {alignmentScore}
                </span>
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                  Alignment
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Constraint Warnings */}
      {isConstraintBreak && !isDisabled && (
        <div className="mb-5 flex items-start gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
          <p className="text-xs text-destructive/90">
            Die dominante Option bricht ein Phase-1-Constraint. Prüfe die Begründung sorgfältig vor dem Commit.
          </p>
        </div>
      )}

      {/* CTA Row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">
            {isDisabled ? "Bewertung abschließen" : "Bereit zum Committen"}
          </h3>
          <p className="text-sm text-muted-foreground">
            {dominantOptionName && !isDisabled ? (
              <>Verbindlichen Commit auf <span className="text-foreground font-medium">{dominantOptionName}</span> starten.</>
            ) : (
              <>Löse alle Konflikte, um fortzufahren.</>
            )}
          </p>
        </div>
        
        <Button 
          onClick={onContinue} 
          size="lg" 
          className={cn(
            "gap-2 min-w-[200px] transition-all",
            !isDisabled && "shadow-md hover:shadow-lg"
          )}
          disabled={isDisabled}
        >
          <Lock className="h-4 w-4" />
          Verbindlichen Commit starten
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
